'use client';

import {
  Badge,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import { useState } from 'react';
import { previewEntryTitleTemplate } from '../../utils/entry-title';
import {
  buildTemplate,
  parseTemplateSegments,
  type TemplateSegment,
} from '../../utils/field-tokens';

interface EntryTitleTemplateFieldProps {
  label: string;
  description?: string;
  value: string;
  fields: { fieldId: string; label: string }[];
  onChange: (value: string) => void;
}

export function EntryTitleTemplateField({
  label,
  description,
  value,
  fields,
  onChange,
}: EntryTitleTemplateFieldProps) {
  const [text, setText] = useState('');

  const segments = parseTemplateSegments(value);
  const fieldLabels = new Map(
    fields.map((field) => [field.fieldId, field.label])
  );

  const commit = (next: TemplateSegment[]) => onChange(buildTemplate(next));

  const addText = () => {
    const literal = text.trim();
    if (!literal) return;
    commit([...segments, { kind: 'text', text: literal }]);
    setText('');
  };

  const preview = previewEntryTitleTemplate(value, fields);

  const seen = new Map<string, number>();
  const chips = segments.map((segment, index) => {
    const id = segment.kind === 'field' ? segment.fieldId : segment.text;
    const occurrence = (seen.get(id) ?? 0) + 1;
    seen.set(id, occurrence);
    return { segment, index, key: `${segment.kind}:${id}:${occurrence}` };
  });

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {description && (
        <p className="txt-xs-regular text-text-tertiary">{description}</p>
      )}

      <div className="flex flex-wrap items-center gap-sm rounded-md border border-border-primary p-sm">
        {segments.length === 0 && (
          <span className="txt-xs-regular text-text-tertiary">
            No title parts yet — insert a field below.
          </span>
        )}
        {chips.map(({ segment, index, key }) => (
          <Badge
            key={key}
            variant={
              segment.kind === 'field' && !fieldLabels.has(segment.fieldId)
                ? 'error'
                : 'gray'
            }
            closeIcon={{
              isShown: true,
              onClose: () =>
                commit(segments.filter((_part, at) => at !== index)),
            }}
          >
            {segment.kind === 'field'
              ? (fieldLabels.get(segment.fieldId) ??
                `Unknown field: ${segment.fieldId}`)
              : segment.text}
          </Badge>
        ))}
      </div>

      <div className="flex items-center gap-sm">
        {fields.length > 0 && (
          <Select
            value=""
            onValueChange={(fieldId) =>
              commit([...segments, { kind: 'field', fieldId }])
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Insert field..." />
            </SelectTrigger>
            <SelectContent>
              {fields.map((field) => (
                <SelectItem key={field.fieldId} value={field.fieldId}>
                  {field.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Input
          value={text}
          placeholder="Add text..."
          onChange={(e) => setText(e.target.value)}
          onBlur={addText}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addText();
            }
          }}
        />
      </div>

      <p className="txt-xs-regular text-text-tertiary">
        Fields resolve against the entry; empty fields are left out of the
        title.
      </p>
      {preview && (
        <p className="txt-xs-regular text-text-secondary">Example: {preview}</p>
      )}
    </div>
  );
}
